import { Block } from '@components/core-ui/Block';
import { Textarea } from '@components/core-ui/form-elements/Textarea';
import PencilIcon from '@components/icons/Pencil.svg';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { Restricted } from '@providers/PermissionProvider/Restricted';
import { routes } from '@routes';
import { RepositoryFilesBlock } from '@pages/User/RepositoryPage/components/RepositoryFilesBlock';
import { PrismaRepositoryAttachmentDto, PrismaUserDto, RepositoriesService } from 'app/API';
import { EACCEPTED_AUDIO_FORMATS } from 'app/types/enums/EACCEPTED_AUDIO_FORMATS';
import { EACCEPTED_DOCUMENT_FORMATS } from 'app/types/enums/EACCEPTED_DOCUMENT_FORMATS';
import { EPermission } from 'app/types/enums/EPermission';
import { useEffect, useRef } from 'react';
import { AssigneeBlockContent } from './styles/AssigneeBlockContent';
import { Input } from '@components/core-ui/form-elements/Input';
import { StyledLink } from './styles/StyledLink';

const slugify = (text: string) => {
  return text
    .replace(/[^a-z0-9]/gi, '-')
    .replace(/-+/g, '-')
    .toLowerCase();
};

export type TForm = {
  name: string;
  slug: string;
  notes: string;
  assignee: PrismaUserDto | null;
  audioFiles: PrismaRepositoryAttachmentDto[];
  documentFiles: PrismaRepositoryAttachmentDto[];
};

type TProps = {
  repositoryId: string;
  organizationSlug: string;
  documentAttachments: PrismaRepositoryAttachmentDto[];
  audioAttachments: PrismaRepositoryAttachmentDto[];
  onDocumentUploadingChange: (isUploading: boolean) => void;
  onAudioUploadingChange: (isUploading: boolean) => void;
  isFilesLoading: boolean;
  form: TForm;
  setForm: (form: TForm) => void;
  slugIsAvailable: boolean;
  setSlugIsAvailable: (isAvailable: boolean) => void;
  isNew: boolean;
};

export const MainBlock = ({
  repositoryId,
  organizationSlug,
  documentAttachments,
  audioAttachments,
  onDocumentUploadingChange,
  onAudioUploadingChange,
  isFilesLoading,
  form,
  setForm,
  slugIsAvailable,
  setSlugIsAvailable,
  isNew,
}: TProps) => {
  const { organization } = useAuthContext();
  const { openModal, closeModal } = useModalContext();
  const { call } = useAPI();
  const slugCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slugAvailabilityRequestIdRef = useRef(0);

  const setSlugAvailabilityIfLatest = (requestId: number, isAvailable: boolean) => {
    if (requestId !== slugAvailabilityRequestIdRef.current) {
      return;
    }
    setSlugIsAvailable(isAvailable);
  };

  const checkSlugAvailability = async (slug: string, requestId: number) => {
    if (slug === '') {
      setSlugAvailabilityIfLatest(requestId, true);
      return;
    }
    if (slug === 'new') {
      setSlugAvailabilityIfLatest(requestId, false);
      return;
    }
    if (!repositoryId || !organization?.slug) {
      setSlugAvailabilityIfLatest(requestId, false);
      return;
    }
    const orgSlug = organization.slug;
    const data = await call(() =>
      RepositoriesService.isRepositorySlugAvailable({
        orgSlug,
        slug,
        repositoryId,
      }),
    );
    setSlugAvailabilityIfLatest(requestId, Boolean(data?.available));
  };

  const triggerSlugAvailabilityCheck = (slug: string, mode: 'debounced' | 'immediate' = 'debounced') => {
    if (slugCheckTimeoutRef.current) {
      clearTimeout(slugCheckTimeoutRef.current);
      slugCheckTimeoutRef.current = null;
    }
    const requestId = slugAvailabilityRequestIdRef.current + 1;
    slugAvailabilityRequestIdRef.current = requestId;
    if (slug === '' || slug === 'new' || !repositoryId || !organization?.slug) {
      void checkSlugAvailability(slug, requestId);
      return;
    }
    if (mode === 'immediate') {
      void checkSlugAvailability(slug, requestId);
      return;
    }
    slugCheckTimeoutRef.current = setTimeout(() => {
      void checkSlugAvailability(slug, requestId);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (slugCheckTimeoutRef.current) {
        clearTimeout(slugCheckTimeoutRef.current);
      }
      slugAvailabilityRequestIdRef.current += 1;
    };
  }, []);

  const editAssignee = (form: TForm) => {
    openModal(ModalNamesEnum.AssignRepositoryModal, {
      repositoryId,
      repository: form,
      onSuccess: (assignee: PrismaUserDto) => {
        setForm({ ...form, assignee });
        closeModal();
      },
    });
  };

  const notesDescription = <p>You can provide additional information</p>;
  const attachmentsDescription = <p>You can provide additional documents, exhibits and audio files.</p>;
  const audioAttachmentsDescription = <p>You can add video and audio files to submit as jobs.</p>;
  const assigneeDescription = <p>You can assign a case to a specific user</p>;

  return (
    <>
      <Block title="Provide a unique title for the case">
        <Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const nextName = e.target.value;
            const nextSlug = slugify(nextName);
            if (isNew && (!form.slug || form.slug === slugify(form.name))) {
              setForm({ ...form, name: nextName, slug: nextSlug });
              triggerSlugAvailabilityCheck(nextSlug, 'debounced');
              return;
            }
            setForm({ ...form, name: nextName });
          }}
          name="name"
          label="Title"
          value={form.name ?? ''}
          required={true}
        />
      </Block>
      <Block title="Case link">
        <Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setForm({ ...form, slug: e.target.value });
          }}
          name="name"
          label="Case ID"
          value={form.slug ?? ''}
          required={true}
          handleBlur={() => {
            triggerSlugAvailabilityCheck(form.slug, 'immediate');
          }}
          error={!slugIsAvailable}
        />
        {!slugIsAvailable ? <div className="text-[var(--color-error-heading)]">Case ID is not available</div> : <></>}
      </Block>
      <Block title="Notes" description={notesDescription}>
        <Textarea
          onChange={(e) => {
            setForm({ ...form, notes: e.target.value });
          }}
          name="notes"
          label="Type your notes here"
          value={form.notes ?? ''}
        />
      </Block>
      <Block title="Attachments" description={attachmentsDescription}>
        {!isFilesLoading ? (
          <RepositoryFilesBlock
            key={`documents-${repositoryId}`}
            repositoryId={repositoryId}
            organizationSlug={organizationSlug}
            accept={EACCEPTED_DOCUMENT_FORMATS.join(', ')}
            attachments={documentAttachments}
            onUploadingChange={onDocumentUploadingChange}
            onAttachmentRemoved={(attachmentId) =>
              setForm({
                ...form,
                documentFiles: form.documentFiles.filter((attachment) => attachment.id !== attachmentId),
              })
            }
          />
        ) : null}
        <div className="uploadDescription">
          Upload your files here. You can upload multiple files at once. Supported file types: .doc, .docx, .txt, .pdf,
          .rtf
        </div>
      </Block>

      <Block title="Audio files" description={audioAttachmentsDescription}>
        {!isFilesLoading ? (
          <RepositoryFilesBlock
            key={`audio-${repositoryId}`}
            repositoryId={repositoryId}
            organizationSlug={organizationSlug}
            accept={EACCEPTED_AUDIO_FORMATS.join(',')}
            attachments={audioAttachments}
            allowPlayback={true}
            onUploadingChange={onAudioUploadingChange}
            onAttachmentRemoved={(attachmentId) =>
              setForm({
                ...form,
                audioFiles: form.audioFiles.filter((attachment) => attachment.id !== attachmentId),
              })
            }
          />
        ) : null}
        <div className="uploadDescription">
          Upload audio files here. You can upload multiple files at once. Supported file types include: Zoom
          multi-channel audio, For The Record (FTR), MP3, WAV and other audio formats.
        </div>
      </Block>

      <Restricted to={EPermission.editRepositories}>
        {repositoryId ? (
          <Block title="Assignee" description={assigneeDescription}>
            <AssigneeBlockContent>
              {form.assignee ? (
                <div className="assignee">
                  <StyledLink to={routes.userDetails.make(organization?.slug ?? '', form.assignee?.id)}>
                    <p className="assignName">
                      {form.assignee?.name} {form.assignee?.lastname}
                    </p>
                    <p className="assignEmail">{form.assignee?.email || ''}</p>
                  </StyledLink>
                </div>
              ) : null}
              <PencilIcon
                className="text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                onClick={() => editAssignee(form)}
              />
            </AssigneeBlockContent>
          </Block>
        ) : null}
      </Restricted>
    </>
  );
};
